/**
 * Signaler un problème qui n'est pas celui d'un exercice.
 *
 * Le bouton de signalement vivait sur l'exercice, et lui seul. Or les défauts
 * qu'on voit le plus ne sont pas dans l'énoncé : la page s'affiche mal, une
 * combinaison de niveau, de difficulté et de variante ne produit rien, la
 * génération tourne sans fin. Ces défauts-là n'avaient aucun geste — il fallait
 * ouvrir un exercice qui, justement, n'existait pas.
 *
 * Ce composant ajoute donc le même geste au niveau de la page. Deux règles :
 *
 * - **On ne demande jamais de contexte technique.** L'adresse, le niveau, le
 *   chapitre, les options de génération, le navigateur, la taille d'écran et la
 *   dernière erreur vue sont relevés ici. Une personne ne doit pas savoir ce
 *   qu'est une variante pour dire que la page ne marche pas.
 * - **Le bouton n'apparaît que si le compte peut signaler.** La même route le
 *   dit à l'interface et le vérifie à l'envoi ; cacher un bouton n'a jamais
 *   protégé une route.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquareWarning, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { createBugReport } from "../lib/adminApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Ce que la personne constate, dit avec ses mots.
 *
 * La liste du signalement d'exercice ne convient pas ici : « réponse fausse »
 * n'a pas de sens sans exercice, et « fallback » ne veut rien dire pour qui
 * n'a pas écrit le produit. Chaque entrée retombe sur une catégorie existante,
 * pour que ces retours se rangent avec les autres.
 */
const CONSTATS = [
  {
    value: "affichage",
    label: "La page s'affiche mal",
    aide: "Texte coupé, chevauchements, mise en page cassée.",
  },
  {
    value: "generation_error",
    label: "Aucun exercice n'est produit",
    aide: "Avec ce niveau, cette difficulté ou cette variante, rien ne sort.",
  },
  {
    value: "fallback",
    label: "Ce n'est pas ce que j'ai demandé",
    aide: "Les exercices ne correspondent pas au chapitre ou aux options choisies.",
  },
  { value: "autre", label: "Autre chose", aide: "" },
];

/** La dernière erreur vue par le navigateur, s'il y en a eu une. */
function useDerniereErreur() {
  const derniere = useRef(null);
  useEffect(() => {
    const surErreur = (e) => {
      derniere.current = String(e?.message || e?.reason || "").slice(0, 300);
    };
    window.addEventListener("error", surErreur);
    window.addEventListener("unhandledrejection", surErreur);
    return () => {
      window.removeEventListener("error", surErreur);
      window.removeEventListener("unhandledrejection", surErreur);
    };
  }, []);
  return derniere;
}

/**
 * @param contexte  Ce que la page sait d'elle-même au moment du clic : niveau,
 *                  chapitre, difficulté, variante, offre. Tout est facultatif —
 *                  un signalement ne se perd jamais sur un contexte incomplet.
 */
export default function SignalerLaPage({ contexte = {}, className = "", autorisation }) {
  const { toast } = useToast();
  const [autorise, setAutorise] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  const [constat, setConstat] = useState("affichage");
  const [texte, setTexte] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const derniereErreur = useDerniereErreur();

  useEffect(() => {
    // H dispose déjà de la capacité retournée par useAuth. Elle se met à jour
    // à la connexion ; aucun second système d'autorisation n'est nécessaire.
    if (typeof autorisation === "boolean") { setAutorise(autorisation); return undefined; }
    let vivant = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/retours/autorise`, {
          credentials: "include",
          headers: (() => {
            const j = localStorage.getItem("lemaitremot_session_token");
            return j ? { "X-Session-Token": j } : {};
          })(),
        });
        const d = await r.json();
        if (vivant) setAutorise(Boolean(d?.autorise));
      } catch (e) {
        if (vivant) setAutorise(false);
      }
    })();
    return () => {
      vivant = false;
    };
  }, [autorisation]);

  const releverLeContexte = useCallback(
    () => ({
      adresse: window.location.href.slice(0, 500),
      niveau: contexte.niveau || null,
      chapitre: contexte.chapitre || null,
      chapitre_libelle: contexte.chapitre_libelle || null,
      difficulte: contexte.difficulte || null,
      variante: contexte.variante || null,
      offre: contexte.offre || null,
      nombre_demande: contexte.nombre_demande ?? null,
      // L'erreur que la page affichait déjà : c'est souvent la seule qui dise
      // vraiment ce qui a échoué, et personne ne la recopie à la main.
      erreur_affichee: contexte.erreur_affichee || null,
      navigateur: navigator.userAgent.slice(0, 300),
      langue: navigator.language || null,
      ecran: `${window.innerWidth}×${window.innerHeight}`,
      derniere_erreur: derniereErreur.current || null,
      moment: new Date().toISOString(),
    }),
    [contexte, derniereErreur]
  );

  const envoyer = async () => {
    setEnvoi(true);
    try {
      const reponse = await createBugReport({
        portee: "page",
        category: constat,
        priority: "medium",
        comment: texte.trim() || null,
        page: releverLeContexte(),
        // Ce que la page connaît déjà part aussi dans les champs dédiés :
        // c'est ce qui permettra de rejouer, quand il y a de quoi rejouer.
        grade: contexte.niveau || null,
        chapter_code: contexte.chapitre || null,
        difficulty: contexte.difficulte || null,
        blueprint_id: contexte.variante || null,
      });
      setOuvert(false);
      setTexte("");
      toast({
        title: `Merci — signalement ${reponse?.ticket_id || "enregistré"}`,
        description:
          "Ce que la page affichait au moment du clic est joint. Vous n'avez rien d'autre à faire.",
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Le signalement n'est pas parti",
        description: String(e?.message || e),
      });
    } finally {
      setEnvoi(false);
    }
  };

  if (!autorise) return null;

  const aide = CONSTATS.find((c) => c.value === constat)?.aide;

  return (
    <>
      <button
        type="button"
        onClick={() => setOuvert(true)}
        className={`inline-flex items-center gap-1.5 text-xs text-gray-500
                    min-h-11 underline-offset-2 transition-colors hover:text-gray-800
                    hover:underline dark:text-gray-400 dark:hover:text-gray-200
                    ${className}`}
      >
        <MessageSquareWarning className="h-3.5 w-3.5" />
        Un problème sur cette page ?
      </button>

      <Dialog open={ouvert} onOpenChange={setOuvert}>
        <DialogContent className="h-dialog sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Un problème sur cette page</DialogTitle>
            <DialogDescription>
              Dites ce que vous constatez. Le reste — l'adresse, vos choix, votre
              navigateur — est joint automatiquement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Ce que je constate</Label>
              <Select value={constat} onValueChange={setConstat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONSTATS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {aide && <p className="text-xs text-gray-500">{aide}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="signalement-page-texte">
                Ce que vous faisiez, en une phrase{" "}
                <span className="font-normal text-gray-400">(facultatif)</span>
              </Label>
              <Textarea
                id="signalement-page-texte"
                rows={3}
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                placeholder="J'ai choisi 6e, difficulté moyenne, et la page reste vide."
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOuvert(false)} disabled={envoi}>
              Annuler
            </Button>
            <Button onClick={envoyer} disabled={envoi}>
              {envoi && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Envoyer le signalement
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
